// Lightweight content-similarity scoring for internal SEO linking.
// Blog ↔ blog and cross-links to /learn topics without external deps.

import { allBlogArticles, type BlogArticle } from "@/data/blogArticles";
import { LEARN_TOPICS, type LearnTopic } from "@/data/learnTopics";
import { isKeeper } from "@/data/blogKeepers";

const STOP = new Set([
  "the", "and", "for", "with", "your", "you", "are", "from", "how", "what",
  "why", "when", "into", "that", "this", "have", "has", "was", "were", "but",
  "not", "any", "all", "can", "get", "out", "our", "their", "there", "them",
  "its", "her", "his", "about", "over", "after", "before", "than", "then",
  "some", "more", "most", "less", "least", "very", "just", "also",
]);

function tokenize(...parts: (string | string[] | undefined)[]): Set<string> {
  const bag = new Set<string>();
  for (const p of parts) {
    if (!p) continue;
    const s = Array.isArray(p) ? p.join(" ") : p;
    for (const raw of s.toLowerCase().split(/[^a-z0-9]+/)) {
      if (raw.length < 3 || STOP.has(raw)) continue;
      bag.add(raw);
    }
  }
  return bag;
}

function score(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  return inter / Math.sqrt(a.size * b.size);
}

function blogTokens(a: BlogArticle) {
  return tokenize(a.title, a.category, a.excerpt, a.keywords);
}
function learnTokens(t: LearnTopic) {
  return tokenize(t.title, t.category, t.description, t.keyPoints);
}

/** Related blog articles for a given blog slug, ranked by keyword overlap. */
export function getRelatedBlogArticles(slug: string, limit = 3): BlogArticle[] {
  const current = allBlogArticles.find((a) => a.slug === slug);
  if (!current) return [];
  const cur = blogTokens(current);
  return allBlogArticles
    .filter((a) => a.slug !== slug && isKeeper(a.slug))
    .map((a) => {
      const bonus = a.category === current.category ? 0.15 : 0;
      return { a, s: score(cur, blogTokens(a)) + bonus };
    })
    .sort((x, y) => y.s - x.s)
    .slice(0, limit)
    .map((x) => x.a);
}

/** Learn topics most relevant to a given blog article (cross-links). */
export function getRelatedLearnTopicsForBlog(slug: string, limit = 2): LearnTopic[] {
  const current = allBlogArticles.find((a) => a.slug === slug);
  if (!current) return [];
  const cur = blogTokens(current);
  return LEARN_TOPICS
    .map((t) => ({ t, s: score(cur, learnTokens(t)) }))
    .filter((x) => x.s > 0)
    .sort((x, y) => y.s - x.s)
    .slice(0, limit)
    .map((x) => x.t);
}

/** Related learn topics for a learn slug: manual list first, auto-fill to N. */
export function getRelatedLearnTopics(slug: string, limit = 3): LearnTopic[] {
  const current = LEARN_TOPICS.find((t) => t.slug === slug);
  if (!current) return [];
  const picked: LearnTopic[] = [];
  const seen = new Set<string>([slug]);
  for (const s of current.relatedSlugs ?? []) {
    const t = LEARN_TOPICS.find((x) => x.slug === s);
    if (t && !seen.has(t.slug)) {
      picked.push(t);
      seen.add(t.slug);
    }
  }
  if (picked.length >= limit) return picked.slice(0, limit);
  const cur = learnTokens(current);
  const auto = LEARN_TOPICS
    .filter((t) => !seen.has(t.slug))
    .map((t) => {
      const bonus = t.category === current.category ? 0.15 : 0;
      return { t, s: score(cur, learnTokens(t)) + bonus };
    })
    .sort((x, y) => y.s - x.s)
    .map((x) => x.t);
  return [...picked, ...auto].slice(0, limit);
}

/** Blog articles most relevant to a given learn topic (cross-links). */
export function getRelatedBlogArticlesForLearn(slug: string, limit = 2): BlogArticle[] {
  const topic = LEARN_TOPICS.find((t) => t.slug === slug);
  if (!topic) return [];
  const cur = learnTokens(topic);
  return allBlogArticles
    .filter((a) => isKeeper(a.slug))
    .map((a) => ({ a, s: score(cur, blogTokens(a)) }))
    .filter((x) => x.s > 0)
    .sort((x, y) => y.s - x.s)
    .slice(0, limit)
    .map((x) => x.a);
}
